/**
 * @file Overlay-header-icon.tsx
 * @module OverlayHeaderIcon
 * @description Pārklājuma (overlay) galvenes ikona Karšu pārlūka uznirstošajā logā.
 *              Attēlo ģeometrijas veida ikonu (punkts, līnija, daudzstūris) ar atbilstošu
 *              krāsu un stilu no ĢIS datu servisa vizualizācijas nosacījumiem.
 *              Atbalsta apvedceļu (detour) un dažādu līniju stilu (dash, contour) attēlošanu.
 */
import { LineString, MultiLineString, Point, Polygon } from "ol/geom";
import { determineHoveredStyle } from "../Feature-highlighter";
import { Feature } from "ol";
import { getMapStore } from "../Map.repository";
import { useContext, useEffect, useState } from "react";
import { useObservable } from "@ngneat/use-observable";
import { getColor, getStyle } from "./Overlay";
import { OverlayManagerContext } from "./Overlay-wrapper";
import arrow from "../../shared/img/arrow.png";
import { FEATURE_LIST } from "../../data-layers/Data-layers-new-layer-manager";

export default function OverlayHeaderIcon({ feat }: { feat: Feature }) {
	const { context } = useContext(OverlayManagerContext);
	const [mapStore] = useObservable(getMapStore());
	const [olStyleState, setOlStyle] = useState();

	useEffect(() => {
		setOlStyle(undefined);
	}, [feat]);

	const getLineIcon = (feature: Feature, style: any | undefined) => {
		if (feature.get("layer").includes("_detour")) {
			return (
				<div className="relative h-11 w-11 shrink-0 overflow-visible">
					<div className="absolute inset-0 h-11 w-[52px] -left-1 flex items-center justify-center">
						<div className="w-full h-2 rounded-full absolute" style={{ background: `#4b4d4f` }}></div>
					</div>
					<div className="absolute inset-0 h-11 w-12 -left-0.5 -right-3 gap-1 flex items-center justify-center">
						{[1, 2, 3].map((i) => (
							<img src={arrow} alt="arrow" key={i} className="h-3 w-3 rounded-full shrink-0" />
						))}
					</div>
				</div>
			);
		}

		switch (style?.stroke_style) {
			case "dash_contour":
				return (
					<div className="absolute inset-0 h-10 w-14 -left-1.5 gap-1 flex flex-col items-center justify-center -z-10">
						<div
							style={{
								backgroundImage: `repeating-linear-gradient(to right,
										${style?.color ?? getColor(feature)},
										${style?.color ?? getColor(feature)} 6px,
										transparent 6px,
										transparent 10px)`,
							}}
							className="w-full h-1.5 rounded-full"
						/>
						<div
							style={{
								backgroundImage: `repeating-linear-gradient(to right,
										${style?.color ?? getColor(feature)},
										${style?.color ?? getColor(feature)} 6px,
										transparent 6px,
										transparent 10px)`,
							}}
							className="w-full h-1.5 rounded-full"
						/>
					</div>
				);
			case "contour":
				return (
					<div className="h-11 w-14 flex flex-col gap-1 justify-center absolute inset-0 -left-1.5 -z-10">
						<div
							style={{
								background: style?.color ?? getColor(feature),
							}}
							className="w-full h-1.5 rounded-full"
						></div>
						<div
							style={{
								background: style?.color ?? getColor(feature),
							}}
							className="w-full h-1.5 rounded-full"
						></div>
					</div>
				);
			case "dot":
				return (
					<div className="h-11 w-11 shrink-0 absolute inset-0 -left-1.5 -z-10">
						{
							<div className="h-11 w-14 flex items-center justify-center gap-1">
								{[1, 2, 3, 4, 5].map((i) => (
									<div
										key={i}
										style={{
											background: style?.color ?? getColor(feature),
										}}
										className="h-2 w-2 rounded-full shrink-0"
									></div>
								))}
							</div>
						}
					</div>
				);
			default:
				return (
					<div className="h-11 w-11 shrink-0 absolute inset-0 -left-1.5 -z-10 overflow-visible">
						{
							<div className="h-11 w-14 flex flex-col justify-center">
								<div
									style={{
										background: style?.color ?? getColor(feature),
									}}
									className="w-full h-1.5 rounded-full"
								></div>
							</div>
						}
					</div>
				);
		}
	};

	const getIcon = (feature: Feature) => {
		const props = feature.getProperties();
		const internal = props["internal"];

		const style = getStyle(feature, internal);

		if (feature.getGeometry() instanceof Polygon && feature.get("layer").includes("kafkamessages_uvismessage") && internal["use_uvis_popups"]) {
			return (
				<div
					className="h-10 w-10 rounded-full flex items-center justify-center"
					style={{
						background: style?.color + "80",
					}}
				>
					<div
						style={{
							background: style?.color,
						}}
						className="h-3 w-3 rounded-full shrink-0"
					></div>
				</div>
			);
		}

		if (style?.piktogram?.includes("c_tonnas_default")) {
			const url = style.piktogram as string;
			const mass = props["mass_restrictions"];
			if (["3.5", "4", "4.5", "5", "5.5", "6", "6.5", "7", "7.5", "8", "8.5", "9", "9.5", ...Array.from({ length: 43 }, (_, i) => `${i + 10}`)].includes(mass)) {
				return <img src={url.replace("/icons/c_tonnas_default.svg", "/tonnas/c_tonnas_" + props["mass_restrictions"] + ".svg")} alt="icon" className="w-11 h-11" />;
			}
			return <img src={url.replace("/icons/c_tonnas_default.svg", "/icons/c_icons_with_white_circle_12.svg")} alt="icon" className="w-11 h-11" />;
		}

		if (
			(props["layer"].includes("kafkamessages_cms") || props["layer"].includes("kafkamessages_camera")) &&
			(style?.piktogram.includes("c_cctv_icon_02") || style?.piktogram.includes("c_cms_temperatura"))
		) {
			const olStyle = olStyleState ?? determineHoveredStyle(mapStore.map!, context.event!, feature, { overrideDetermined: !!feature.get("overrideDetermined") });
			if (!olStyleState) {
				setOlStyle(olStyle);
			}
			const url = (olStyle?.[0]?.getImage() as any)?.iconImage_.src_ ?? "";

			if (url.includes("c_cms_temperatura")) {
				const temp = feature.get("t1");
				const status = feature.get("status");

				return (
					<div className="flex items-center justify-center h-11 w-11">
						<div className={`bg-[#4b4d4f] h-5 w-11 text-white flex text-sm justify-center font-semibold items-center rounded-md`}>
							<p>{status === "active" ? (temp ? `${temp > 0 ? "+" : ""}${Number(temp).toFixed(0)}°` : "") : "-"}</p>
						</div>
					</div>
				);
			} else {
				return <img src={url} alt="icon" className="w-11 h-11" />;
			}
		}

		const url: string = internal?.["icon"] ?? Array.isArray(style) ? style?.find((s: any) => !!s.piktogram)?.piktogram : style?.piktogram;

		if (feature.getGeometry() instanceof Point) {
			const linkedFeature = FEATURE_LIST[props["linked_to"] + "-" + props["layer"] + "-LineString"]?.feature;
			if (linkedFeature) {
				const style = getStyle(linkedFeature, internal);
				return (
					<div className="relative h-11 w-11">
						{url && <img src={url} alt="icon" className="w-11 h-11 z-50" />}
						{getLineIcon(linkedFeature, style)}
					</div>
				);
			}

			if (url) {
				return <img src={url} alt="icon" className="w-11 h-11" />;
			} else {
				return (
					<div className="w-11 h-11 flex items-center justify-center">
						<div className="rounded-full w-4 h-4" style={{ background: style?.color }}></div>
					</div>
				);
			}
		}

		if (feature.getGeometry() instanceof LineString || feature.getGeometry() instanceof MultiLineString) {
			return (
				<div className="relative h-11 w-11">
					{url && !feature.get("layer").includes("_detour") && <img src={url} alt="icon" className="w-11 h-11 z-50" />}
					{getLineIcon(feature, style)}
				</div>
			);
		}
	};

	return <>{getIcon(feat)}</>;
}
